import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Menu, MenuItem, Stack, Typography } from '@mui/material'
import { RefreshCw, Link2, Lock, Check, type LucideIcon } from 'lucide-react'

import { MENU_PAPER_SX } from '@repo/ui'

import { useReferrerState } from 'hooks'
import { ROUTES } from 'routes'

import type { ProductSyncMode } from 'types'

export interface ProductSyncModeDef {
  value: ProductSyncMode
  label: string
  desc: string
  /** Same glyph is used for a mode in both the trigger and its menu row (VRG-103) —
   *  keeps the two readable as one system rather than every mode looking identical. */
  icon: LucideIcon
}

/**
 * VRG-103 — one icon per auto-sync mode, chosen to match language already used
 * elsewhere in the product (not arbitrary):
 *  - `inherit` → Link2: the product is LINKED to a setting that lives elsewhere
 *    (the company's global sync config), which is exactly what the row's own
 *    description says ("Follows your company's global sync setting").
 *  - `always_sync` → RefreshCw: continuous/active syncing. Kept as the pre-existing
 *    icon — it's also the one used for the manual "Sync now" spinner, so "always
 *    sync" and "syncing right now" share a glyph on purpose.
 *  - `locked` → Lock: matches the word "Locked" already used in this product's own
 *    copy (see ProductEditPage's syncNotice, which literally says "This product is
 *    locked").
 */
export const PRODUCT_SYNC_MODES: ProductSyncModeDef[] = [
  {
    value: 'inherit',
    label: 'Use store default',
    desc: "Follows your company's global sync setting.",
    icon: Link2,
  },
  {
    value: 'always_sync',
    label: 'Always sync',
    desc: 'Store changes always update this product.',
    icon: RefreshCw,
  },
  {
    value: 'locked',
    label: 'Never sync',
    desc: 'Store changes never overwrite this product.',
    icon: Lock,
  },
]

export const getProductSyncMode = (mode?: ProductSyncMode | null): ProductSyncModeDef =>
  PRODUCT_SYNC_MODES.find((m) => m.value === mode) ?? PRODUCT_SYNC_MODES[0]

interface ProductSyncModeMenuProps {
  /** Current per-product auto-sync policy (VRG-102 sync_mode). */
  mode: ProductSyncMode
  /** Change the persistent auto-sync policy; persists per product. */
  onChangeMode: (mode: ProductSyncMode) => void
  /** Renders the clickable control that opens the menu. Kept as a render prop
   *  (rather than a fixed trigger) because the two consumers need different
   *  triggers around the SAME menu — the product page's icon-only split-button
   *  caret vs. the edit-page warning alert's labelled inline control — while
   *  the menu itself (rows, icons, the inherit deep-link, the selected check)
   *  must never fork between them (working-conventions §36). */
  renderTrigger: (props: {
    onClick: (e: React.MouseEvent<HTMLElement>) => void
    current: ProductSyncModeDef
  }) => React.ReactNode
}

/**
 * VRG-102/VRG-103 — the shared "Auto-sync" mode picker. Used by BOTH
 * `ProductSyncButton` (the product page's split-button caret) and
 * `ProductEditPage`'s Shopify sync warning alert, so the mode control can never
 * drift between the two surfaces that show it.
 */
export const ProductSyncModeMenu = ({ mode, onChangeMode, renderTrigger }: ProductSyncModeMenuProps) => {
  const navigate = useNavigate()
  // Carries "← Back to Product" to the field-mapping page.
  const referrer = useReferrerState('Product')
  const [anchor, setAnchor] = useState<null | HTMLElement>(null)
  const current = getProductSyncMode(mode)

  return (
    <>
      {renderTrigger({ onClick: (e) => setAnchor(e.currentTarget), current })}

      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              minWidth: 280,
              borderRadius: '16px',
              // House menu padding convention (MENU_PAPER_SX).
              ...MENU_PAPER_SX,
              '& .MuiMenu-list': {
                pt: 0,
                pb: 0,
              },
            },
          },
        }}
      >
        {PRODUCT_SYNC_MODES.map((m) => {
          const Icon = m.icon
          return (
            <MenuItem
              key={m.value}
              selected={mode === m.value}
              onClick={() => {
                if (m.value !== mode) onChangeMode(m.value)
                setAnchor(null)
              }}
              sx={{
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 2,
                // Rows are two-line (icon + label + description) — the house
                // MENU_ITEM_SX (p:3/12px) reads cramped for that much content, so
                // this menu is given a taller local vertical inset instead of
                // widening the shared constant every menu in the app depends on.
                // Horizontal stays at the house 12px so it still lines up with
                // the paper's own inset (MENU_PAPER_SX).
                px: 3,
                py: 4,
                borderRadius: 1.5,
                // Matches the house Select's item-gap convention (Select.tsx) — these
                // rows are two-line (label + description), so a small gap keeps them
                // from reading as one dense block.
                mb: '4px',
                '&:last-of-type': { mb: 0 },
              }}
            >
              <Stack direction="row" spacing={3} alignItems="flex-start" sx={{ minWidth: 0 }}>
                <Box sx={{ pt: '2px', display: 'flex', flexShrink: 0, color: 'text.secondary' }}>
                  <Icon strokeWidth={1} size={18} />
                </Box>
                <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                  <Typography variant="body3">{m.label}</Typography>
                  <Typography variant="body4" color="text.secondary">
                    {m.value === 'inherit' ? (
                      <>
                        Follows your{' '}
                        <Box
                          component="span"
                          onClick={(e) => {
                            // Don't let the row's onClick also fire (would re-select the mode).
                            e.stopPropagation()
                            setAnchor(null)
                            // Settings → Integrations → Shopify, which is where the
                            // "Sync value updates" switch this text refers to now lives.
                            // NOT SETTINGS_COMPANY_FIELDMAPPING_LEGACY: main moved field
                            // mapping under the per-integration page and renamed the old
                            // standalone route to _LEGACY, so this link broke on the merge
                            // — caught by typecheck, which is the one thing that would.
                            navigate(
                              `${ROUTES.SETTINGS_COMPANY_INTEGRATION('shopify')}?highlight=sync`,
                              { state: referrer },
                            )
                          }}
                          sx={{
                            // House reference-link style: white (inherits body text), underlined,
                            // and carries a referrer so field mapping shows "← Back to Product".
                            color: 'text.primary',
                            textDecoration: 'underline',
                            textUnderlineOffset: '2px',
                            cursor: 'pointer',
                          }}
                        >
                          company&apos;s global sync setting
                        </Box>
                        .
                      </>
                    ) : (
                      m.desc
                    )}
                  </Typography>
                </Stack>
              </Stack>
              {/* Right-aligned selected check — inherits text.primary (white in dark mode),
                  matching the house SelectField check. */}
              <Box sx={{ pt: '2px', display: 'flex', flexShrink: 0 }}>
                {mode === m.value && <Check strokeWidth={2} size={18} />}
              </Box>
            </MenuItem>
          )
        })}
      </Menu>
    </>
  )
}
